import { useState, useEffect } from "react";
import { stripUuid, download, briefToMarkdown, briefToCsv, downloadExcel } from "./complianceExport";
import API_BASE_URL from "../../config";
import DataTable from "./DataTable";
import "./CompliancePipeline.css";
import "./ApprovalAnalysis.css";

// Source locations are free text like "Seite 3", "Tabelle 1, §2.1.2(2)".
// Pull a 1-indexed page number out so we can deep-link into the PDF when we
// have a document URL; otherwise the location renders as plain text.
const PAGE_RE = /(?:Seite|Page|S\.)\s*(\d+)/i;

// Mirrors discovery.CRITICAL_GAP_PREFIX — critical gaps (e.g. a norm section with
// no extracted requirements, a possible exclusion condition) are shown in red.
const CRITICAL_GAP_PREFIX = "⛔ KRITISCH:";
const EMPTY_CELL = new Set(["", "-", "–", "—", "n/a", "na", "none", "null"]);
const isEmptyCell = (v) => v == null || EMPTY_CELL.has(String(v).trim().toLowerCase());

// A "Spalte N" header is a placeholder the re-detect emitted when it couldn't read
// the real column heading. Never show it bare in production — render it as an honest
// "unbenannt" marker that points the engineer to the source page.
const PLACEHOLDER_COL = /^Spalte \d+$/;
const isPlaceholderCol = (c) => PLACEHOLDER_COL.test((c || "").trim());

function SourceCell({ location, documentUrl }) {
  const text = location || "—";
  if (!documentUrl || !location) return <span>{text}</span>;
  const match = location.match(PAGE_RE);
  const href = match ? `${documentUrl}#page=${match[1]}` : documentUrl;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="cp-source-link">
      {text} ↗
    </a>
  );
}

/**
 * Presentational compliance/discovery brief — shared by the live DiscoveryView
 * and the read-only SavedRunView so both render identically (DRY).
 *
 * Props:
 *   brief        — { document_name, document_type, sections[], gaps[] }
 *   documentUrl  — optional; when set, the Quelle column deep-links into the PDF
 *   headExtra    — optional node rendered in the head (e.g. an Approve button)
 */
export default function ComplianceBrief({ brief, documentUrl = "", headExtra = null, documentId = "", database = "", onRevise = null }) {
  // Z-document figure images (diagrams, drawings) for THIS document — the PDF
  // requires answers be backed by the source figures, not text alone. Fetched
  // lazily from the shared assets endpoint so the table view and the
  // approval-analysis view show the same images (DRY).
  const [images, setImages] = useState([]);
  const [pageImages, setPageImages] = useState([]);
  // Which extracted sections the engineer wants to see. We track HIDDEN ids (empty
  // = show all) so newly-extracted sections default to visible; reset when the
  // document changes so one document's choices don't leak into another.
  const [hidden, setHidden] = useState(() => new Set());
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => { setHidden(new Set()); }, [documentId]);
  useEffect(() => {
    let cancelled = false;
    if (!documentId) { setImages([]); setPageImages([]); return undefined; }
    fetch(`${API_BASE_URL}/api/v1/approval/z-document-assets`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ document_id: documentId, database: database || "Betonbau" }),
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        if (cancelled) return;
        setImages(Array.isArray(d.images) ? d.images : []);
        setPageImages(Array.isArray(d.page_images) ? d.page_images : []);
      })
      .catch(() => { if (!cancelled) { setImages([]); setPageImages([]); } });
    return () => { cancelled = true; };
  }, [documentId, database]);

  if (!brief) return null;
  const sections = Array.isArray(brief.sections) ? brief.sections : [];
  const gaps = Array.isArray(brief.gaps) ? brief.gaps : [];
  const name = stripUuid(brief.document_name);

  const sectionId = (s, i) => s.key || `sec-${i}`;
  const sectionTitle = (s) => s.title || s.label || s.key || "Abschnitt";
  const isVisible = (s, i) => !hidden.has(sectionId(s, i));
  const toggle = (id) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const visibleCount = sections.filter(isVisible).length;

  return (
    <div className="ds-card cp-card">
      <div className="ds-section-title cp-card-head">
        {name}
        {brief.document_type && <span className="ds-badge">{brief.document_type}</span>}
        <span className="cp-head-actions">
          {headExtra}
          <button
            className="ds-btn ds-btn--secondary ds-btn--sm"
            onClick={() => download(`${name}.md`, briefToMarkdown(brief), "text/markdown")}
          >
            ⬇ Markdown
          </button>
          <button
            className="ds-btn ds-btn--secondary ds-btn--sm"
            onClick={() => download(`${name}.csv`, briefToCsv(brief), "text/csv")}
          >
            ⬇ CSV
          </button>
          {documentId && (
            <button
              className="ds-btn ds-btn--secondary ds-btn--sm"
              onClick={() => downloadExcel(API_BASE_URL, documentId, database, `${name}.xlsx`).catch((e) => alert(e.message))}
            >
              ⬇ Excel
            </button>
          )}
          {onRevise && brief.gaps?.length > 0 && (
            <button className="ds-btn ds-btn--secondary ds-btn--sm" onClick={onRevise}>
              🔁 Tabellen neu erkennen
            </button>
          )}
        </span>
      </div>

      {sections.length > 1 && (
        <div className="cp-picker" role="group" aria-label="Abschnitte anzeigen">
          <span className="cp-picker-label">Anzeigen ({visibleCount}/{sections.length}):</span>
          {sections.map((s, i) => {
            const id = sectionId(s, i);
            const on = isVisible(s, i);
            return (
              <button key={id} type="button"
                className="ds-chip cp-chip"
                aria-pressed={on}
                onClick={() => toggle(id)}>
                {on ? "✓ " : ""}{sectionTitle(s)}
              </button>
            );
          })}
          <span className="cp-head-actions">
            <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm" onClick={() => setHidden(new Set())}>Alle</button>
            <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm"
              onClick={() => setHidden(new Set(sections.map(sectionId)))}>Keine</button>
          </span>
        </div>
      )}

      {gaps.length > 0 && (() => {
        const critical = gaps.filter((g) => g.startsWith(CRITICAL_GAP_PREFIX));
        const normal = gaps.filter((g) => !g.startsWith(CRITICAL_GAP_PREFIX));
        return (
          <div className="cp-missing">
            <div className="cp-missing-head">
              Fehlende / zu prüfende Angaben — {gaps.length}
              {critical.length > 0 && <span className="cp-missing-crit-count"> · {critical.length} kritisch</span>}
              <button type="button" className="cp-help-btn" aria-expanded={helpOpen}
                title="Was bedeuten diese Hinweise?" onClick={() => setHelpOpen((v) => !v)}>
                ?
              </button>
            </div>
            <p className="cp-missing-hint">
              Diese Werte fehlen in der Extraktion oder konnten nicht sicher erkannt werden.
              Bitte in der angegebenen Quelle (Seitenbild unten) prüfen.
            </p>
            {helpOpen && (
              <div className="cp-help">
                <p><b className="cp-gap-crit">⛔ Kritisch</b> — könnte beeinflussen, <i>ob</i> die
                  Zulassung gilt (z.&nbsp;B. fehlende Ausschluss-/Anwendungsbedingung oder ein
                  Abschnitt ohne Ergebnis). <b>Immer manuell im Quelldokument prüfen.</b></p>
                <p><b>▢ Hinweis</b> — einzelne Werte wurden nicht erfasst. Mit dem Seitenbild
                  abgleichen oder „🔁 Tabellen neu erkennen" nutzen. Die angezeigten Daten sind
                  weiterhin verwendbar, nur als ggf. unvollständig markiert.</p>
                <ul className="cp-help-list">
                  <li><b>Abschnitt … keine Anforderungen extrahiert</b> — Textabschnitt erkannt,
                    aber leer ausgelesen → mögliche Ausschlussbedingung, Seite prüfen.</li>
                  <li><b>Abschnitt … lieferte kein Ergebnis</b> — Extraktion für diesen Abschnitt
                    fehlgeschlagen → neu extrahieren / Seitenbereich prüfen.</li>
                  <li><b>Tabelle … keine Datenzeilen erkannt</b> — meist rahmenlose Tabelle/Zeichnung
                    → neu erkennen oder Seitenbild lesen.</li>
                  <li><b>Tabelle … A von B Werten — Z fehlen</b> — fehlende Zellen sind <span
                    className="cp-cell-missing">rot</span> markiert → mit Seite N abgleichen.</li>
                </ul>
                <p className="cp-missing-hint">
                  Badges: <b>📐 neu erkannt</b> = direkt aus dem PDF (zuverlässiger) ·
                  <b> 🔍 Vision — prüfen</b> = vom Vision-Modell gelesen, bitte verifizieren.
                </p>
              </div>
            )}
            {critical.map((g, i) => <div key={`c${i}`} className="cp-gap-crit">{g}</div>)}
            {normal.map((g, i) => <div key={`n${i}`}>▢ {g}</div>)}
          </div>
        );
      })()}

      {(pageImages.length > 0 || images.length > 0) && (
        <div className="aa-figures">
          <h4 className="cp-section-head">
            Quelle aus dem Z-Dokument ({pageImages.length + images.length})
          </h4>
          <div className="aa-figure-grid">
            {pageImages.map((p, i) => (
              <a key={`p${i}`} href={`${API_BASE_URL}${p.url}`} target="_blank" rel="noreferrer"
                 className="aa-figure" title={`Seite ${p.page_number}`}>
                <img src={`${API_BASE_URL}${p.url}`} alt={`Seite ${p.page_number}`} loading="lazy" />
                <span className="cp-figure-cap">Seite {p.page_number}</span>
              </a>
            ))}
            {images.map((url, i) => (
              <a key={`f${i}`} href={`${API_BASE_URL}${url}`} target="_blank" rel="noreferrer" className="aa-figure">
                <img src={`${API_BASE_URL}${url}`} alt={`Abbildung ${i + 1}`} loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      {sections.map((s, i) => {
        if (!isVisible(s, i)) return null;
        const kind = s.kind || "tabular";
        if (kind === "prose") {
          const reqs = Array.isArray(s.requirements) ? s.requirements : [];
          return (
            <div key={i} className="cp-section">
              <h4 className="cp-section-head">
                <span className="cp-section-title">{sectionTitle(s)}</span>{" "}
                <span className="cp-src">({reqs.length} Anforderungen)</span>
              </h4>
              {s.description && <p className="cp-desc">{s.description}</p>}
              <ol className="cp-prose-list">
                {reqs.map((r, j) => (
                  <li key={j}>
                    {r.text}{" "}
                    <span className="cp-src">
                      [<SourceCell location={r.source?.location} documentUrl={documentUrl} />]
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          );
        }
        const columns = Array.isArray(s.columns) ? s.columns : [];
        const rows = Array.isArray(s.rows) ? s.rows : [];
        return (
          <div key={i} className="cp-section">
            <h4 className="cp-section-head">
              <span className="cp-section-title">{sectionTitle(s)}</span>{" "}
              <span className="cp-src">({rows.length} Zeilen)</span>
              {s.missing > 0 && (
                <span className="ds-badge ds-badge--warning" title="Vollständigkeitsprüfung: nicht alle Werte der Quelltabelle wurden extrahiert">
                  ⚠ {s.missing} fehlen ({s.extracted_cells}/{s.source_cells})
                </span>
              )}
              {s.provenance === "pdf_tables" && (
                <span className="ds-badge ds-badge--info" title="Direkt aus dem PDF neu erkannt (nicht aus dem OCR-Text)">📐 neu erkannt</span>
              )}
              {s.provenance === "vision" && (
                <span className="ds-badge ds-badge--warning" title="Vom Vision-Modell gelesen — bitte prüfen">🔍 Vision — prüfen</span>
              )}
            </h4>
            {s.description && <p className="cp-desc">{s.description}</p>}
            <DataTable>
              <thead>
                <tr>
                  {columns.map((c, j) => (
                    isPlaceholderCol(c)
                      ? <th key={j} className="cp-col-unnamed"
                            title="Spaltenüberschrift konnte nicht erkannt werden — bitte im Seitenbild prüfen">
                          unbenannt ⚠
                        </th>
                      : <th key={j}>{c}</th>
                  ))}
                  <th>Quelle</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, j) => (
                  <tr key={j}>
                    {columns.map((c, k) => {
                      const v = r.cells?.[c];
                      // Highlight empty value cells ONLY in tables flagged incomplete,
                      // and never the first (label) column — so the engineer sees
                      // exactly which values are missing, without noise on healthy tables.
                      const flag = s.missing > 0 && k > 0 && isEmptyCell(v);
                      return <td key={k} className={flag ? "cp-cell-missing" : undefined}
                                 title={flag ? "Wert fehlt — in der Quelle prüfen" : undefined}>
                        {isEmptyCell(v) ? "—" : v}
                      </td>;
                    })}
                    <td className="cp-src">
                      <SourceCell location={r.source?.location} documentUrl={documentUrl} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        );
      })}
    </div>
  );
}
